import { IMAGE_MIME_TYPES, TRANSPARENT_OPTION } from 'pages/Batch/configs';
import computeImageOffsets from 'utils/canvas/computeImageOffsets';
import { ObjectPosition } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/MobilePanel/constants';
import type { TBackgroundProp } from 'pages/Batch/types';
import { getResizingParams } from 'pages/Batch/utils';
import type { IForegroundContentBbox, IForegroundTransform } from 'types/store';
import { FULL_CONTENT_BBOX } from 'types/store';
import computeForegroundBbox from 'utils/canvas/computeForegroundBbox';

import applyCanvasSizeLimit from '../applyCanvasSizeLimit';
import type { IProcessResult } from './toolchain';

const hasRatio = (ratio?: { width: number; height: number } | null): ratio is { width: number; height: number } => Boolean(ratio?.width && ratio?.height);

const isIdentityTransform = (t: IForegroundTransform | null | undefined) => !t || (t.tx === 0 && t.ty === 0 && t.sx === 1 && t.sy === 1 && t.rotation === 0);

// Scale + translate around the content-bbox center (matches preview transform-origin).
// tx/ty are fractions of the image rect — multiply by drawWidth/Height to get canvas pixels.
const applyScaleTranslate = (x: number, y: number, drawWidth: number, drawHeight: number, t: IForegroundTransform, bbox: IForegroundContentBbox) => {
  const cx = ((bbox.minX + bbox.maxX) / 2) * drawWidth;
  const cy = ((bbox.minY + bbox.maxY) / 2) * drawHeight;
  return {
    x: x + cx * (1 - t.sx) + t.tx * drawWidth,
    y: y + cy * (1 - t.sy) + t.ty * drawHeight,
    drawWidth: drawWidth * t.sx,
    drawHeight: drawHeight * t.sy,
  };
};

// Draws image with full foreground transform: scale + translate + rotation around the
// content-bbox center. Rotation pivot is computed in canvas coords after the scale+translate
// so it matches the CSS transform `translate(tx) rotate(deg) scale(sx,sy)` with origin at the
// content-bbox center.
const drawForegroundTransformed = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  baseX: number,
  baseY: number,
  baseW: number,
  baseH: number,
  transform: IForegroundTransform | null | undefined,
  bbox: IForegroundContentBbox,
) => {
  if (isIdentityTransform(transform)) {
    ctx.drawImage(image, 0, 0, image.width, image.height, baseX, baseY, baseW, baseH);
    return;
  }
  const t = transform as IForegroundTransform;
  const dst = applyScaleTranslate(baseX, baseY, baseW, baseH, t, bbox);
  // Pivot = content-bbox center in destination-rect coords AFTER scale+translate.
  const pivotX = dst.x + ((bbox.minX + bbox.maxX) / 2) * dst.drawWidth;
  const pivotY = dst.y + ((bbox.minY + bbox.maxY) / 2) * dst.drawHeight;
  if (t.rotation === 0) {
    ctx.drawImage(image, 0, 0, image.width, image.height, dst.x, dst.y, dst.drawWidth, dst.drawHeight);
    return;
  }
  ctx.save();
  ctx.translate(pivotX, pivotY);
  ctx.rotate((t.rotation * Math.PI) / 180);
  ctx.translate(-pivotX, -pivotY);
  ctx.drawImage(image, 0, 0, image.width, image.height, dst.x, dst.y, dst.drawWidth, dst.drawHeight);
  ctx.restore();
};

const getRatioPlacement = (canvasWidth: number, canvasHeight: number, drawWidth: number, drawHeight: number, position?: ObjectPosition | null) => {
  let x: number;
  let y: number;

  switch (position) {
    case ObjectPosition.TOP_LEFT:
    case ObjectPosition.BOTTOM_LEFT:
      x = 0;
      break;
    case ObjectPosition.TOP_RIGHT:
    case ObjectPosition.BOTTOM_RIGHT:
      x = canvasWidth - drawWidth;
      break;
    default:
      x = (canvasWidth - drawWidth) / 2;
  }

  switch (position) {
    case ObjectPosition.TOP_LEFT:
    case ObjectPosition.TOP:
    case ObjectPosition.TOP_RIGHT:
      y = 0;
      break;
    case ObjectPosition.BOTTOM_LEFT:
    case ObjectPosition.BOTTOM:
    case ObjectPosition.BOTTOM_RIGHT:
      y = canvasHeight - drawHeight;
      break;
    default:
      y = (canvasHeight - drawHeight) / 2;
  }

  return { x, y };
};

const backgroundProcess = async (image: HTMLImageElement, { texture, color, objectPosition, ratio, foregroundTransform, foregroundContentBbox }: TBackgroundProp): Promise<IProcessResult> => {
  const hasTransform = !isIdentityTransform(foregroundTransform);
  if (!texture && !objectPosition && !hasRatio(ratio) && !hasTransform && (!color || color === TRANSPARENT_OPTION)) {
    return new Promise(resolve => {
      resolve({ result: image.src });
    });
  }
  // Bbox is only meaningful when a non-identity transform is applied. Scan now if the caller
  // didn't pass one (e.g. user applies before opening preview).
  const bbox: IForegroundContentBbox = hasTransform ? foregroundContentBbox ?? (await computeForegroundBbox(image)) : FULL_CONTENT_BBOX;

  const canvas = document.createElement('canvas');
  const baseDims = hasRatio(ratio) ? { width: ratio.width, height: ratio.height } : { width: image.width, height: image.height };
  const { width: newWidth, height: newHeight } = applyCanvasSizeLimit(baseDims.width, baseDims.height);
  canvas.width = newWidth;
  canvas.height = newHeight;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    if (color && color !== TRANSPARENT_OPTION) {
      ctx.fill();
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    if (texture) {
      const { fromX, fromY, width: fWidth, height: fHeight } = getResizingParams(newWidth, newHeight, texture.width, texture.height);

      ctx.drawImage(texture, fromX, fromY, fWidth || texture.width, fHeight || texture.height, 0, 0, newWidth, newHeight);
    }

    if (hasRatio(ratio)) {
      const scale = Math.min(newWidth / image.width, newHeight / image.height);
      const baseDrawWidth = image.width * scale;
      const baseDrawHeight = image.height * scale;
      const { x: baseX, y: baseY } = getRatioPlacement(newWidth, newHeight, baseDrawWidth, baseDrawHeight, objectPosition);
      drawForegroundTransformed(ctx, image, baseX, baseY, baseDrawWidth, baseDrawHeight, foregroundTransform, bbox);
    } else if (objectPosition) {
      const offset = await computeImageOffsets(image, objectPosition);
      drawForegroundTransformed(ctx, image, offset.offsetX, offset.offsetY, image.width, image.height, foregroundTransform, bbox);
    } else {
      drawForegroundTransformed(ctx, image, 0, 0, image.width, image.height, foregroundTransform, bbox);
    }
  }

  return new Promise(resolve => {
    canvas.toBlob(blob => blob && resolve({ blob, result: URL.createObjectURL(blob) }), IMAGE_MIME_TYPES.WEBP);
  });
};

export default backgroundProcess;
